import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useContent } from 'contexts/content-context';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import { Grid } from '@mui/material';

import {
  EmptyState,
  FilterButton,
  Loading,
  SaveFilterBlock
} from 'components/common/Elements/Elements';
import InputSearch from 'components/common/forms/InputSearch/InputSearch';
import { useRegionPersistedSearch } from 'contexts/SearchPersistenceContext';
import { PageTitle, DataGrid } from '@p81-common/ui';

import {
  defaultFilters,
  defaultNavigation,
  exportConfig
} from './helpers/configurates';
import { createReqBody } from './helpers/helpers';
import { postRequest, getRequest } from 'utils/requests';
import {
  checkingForExistenceOfOptions,
  replaceHistoryCML,
  parsConfigForHistory,
  reParsConfigForHistory,
  clearOrDefaultFilters,
  onChangeExportButton
} from 'utils/common';

import { isInProgressCMStatus } from 'utils/cloudManagement';

import { useDataGrid } from '../../../hooks/useDataGrid';
import FiltersBlock from './parts/FiltersBlock/FiltersBlock';
import { useTableSettings } from './helpers/tableSettings';

import classes from './CloudManagementLogs.module.scss';

const REQUEST_FOR_FILTERED_DATA_DELAY = 200;

/*
 * Fallback refresh while any row is unfinished.
 *
 * Live updates arrive only over the beaconStatus socket, so if that drops — or
 * the worker stops emitting, which is exactly what a stuck job looks like — a
 * row keeps whatever state it had when the page loaded and the user has no way
 * to tell a wedged action from a stale screen. Slow on purpose: this is a safety
 * net for the socket, not the primary update path.
 */
const IN_PROGRESS_POLL_INTERVAL = 30000;

const CloudManagementLogs = () => {
  const { t } = useTranslation(['activity']);
  const { contentState } = useContent();

  const history = useHistory();
  const isInitialized = useRef(false);
  const unmount = useRef(false);

  /*
   * Only the most recently issued list request may write to the table. The HTTP
   * client's timeout is 40s (utils/jarvisApiClient.js) and the in-progress poll
   * fires every 30s, so requests genuinely overlap: without this, a poll issued
   * for the previous page or filter can resolve after the user's newer fetch and
   * repaint the table with rows they are no longer looking at.
   */
  const latestRequestId = useRef(0);

  // Lazily referenced: requestForFilteredData is declared further down, and this
  // only ever runs from a click handler, never during render.
  const { tableConfiguration, confirmModal } = useTableSettings(() =>
    requestForFilteredData()
  );
  const {
    handleTableSettingsMenuItemClick,
    handleSearchChangeCallback,
    handleChangeNumOfRows,
    setFilter,
    filter,
    onSortEnd,
    rowSelectionChanged,
    tableSettingsDisplay,
    changeCurrentPage,
    numOfRowsOptions,
    tableSettingsChanged,
    results,
    setResults,
    tableData,
    setTableData,
    tableConfigDisplay,
    loaderTable,
    setLoaderTable,
    direction,
    setDirection,
    startFilter,
    setStartFilter,
    handleFilterInput,
    applySearch,
    filterButton,
    setFilterButton,
    handleFilterButton,
    onClearSearch,
    filterBackend,
    setFilterBackend,
    visibleFilterSave,
    setVisibleFilterSave,
    visibleFilterEdit,
    setVisibleFilterEdit,
    changeFilter,
    setChangeFilter,
    trigerResetDatePicker: triggerResetDatePicker,
    setTrigerResetDatePicker,
    tableConf,
    setTableConf,
    filters,
    setFilters,
    loader,
    setLoader,
    dataConection: dataConnection,
    setDataConection: setDataConnection,
    selectLoader,
    setSelectLoader,
    setPrevStateFilter,
    setPrevStateTableConf,
    triggerSaveTableSettings
  } = useDataGrid(tableConfiguration, defaultFilters);

  // Keep the typed search when switching DR region (P81-132768).
  useRegionPersistedSearch(filter.search, applySearch);

  useEffect(() => {
    if (!isInitialized.current) return;

    replaceHistoryCML(
      direction,
      filter,
      undefined,
      history,
      false,
      false,
      changeFilter,
      parsConfigForHistory(tableConf)
    );

    // eslint-disable-next-line
  }, [startFilter]);

  useEffect(() => {
    setLoaderTable(true);
    const query = new URLSearchParams(history.location.search);
    const queryFilter = query.get('filter') || '';
    const queryTableConf = query.get('tableConf') || '';
    if (!queryFilter) {
      const isDefaultFilter = isEqual(filter, {
        ...defaultFilters,
        ...defaultNavigation
      });
      if (isDefaultFilter) {
        replaceHistoryCML(
          false,
          filter,
          undefined,
          history,
          'replace',
          false,
          'Default',
          parsConfigForHistory(tableConf)
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
        setPrevStateFilter(filter);
        setPrevStateTableConf(tableConf);
        delayedRequestForFilteredData();
      } else {
        resetAllfilters(true);
        replaceHistoryCML(
          false,
          filter,
          undefined,
          history,
          'replace',
          {
            ...defaultFilters,
            ...defaultNavigation
          },
          'Default',
          tableConf
        );
        setVisibleFilterEdit(false);
        setVisibleFilterSave(false);
      }
    } else {
      let parsedFilter;
      try {
        parsedFilter = JSON.parse(queryFilter);
      } catch (err) {
        !unmount.current && console.log(err);
      }
      setFilter(parsedFilter);
      delayedRequestForFilteredData(parsedFilter);

      if (queryTableConf) {
        setPrevStateTableConf(
          reParsConfigForHistory(JSON.parse(queryTableConf))
        );
        setTableConf(
          reParsConfigForHistory(JSON.parse(queryTableConf), tableConfigDisplay)
        );
      } else {
        setPrevStateTableConf(tableConf);
        setTableConf(tableConfigDisplay);
      }
    }
    setTrigerResetDatePicker(!triggerResetDatePicker);
    isInitialized.current = true;

    // eslint-disable-next-line
  }, [history.location.search]);

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  /*
   * `isStale` is inherited from the list request that spawned this one.
   *
   * Guarding only on unmount was not enough. This runs from the list request's
   * success handler, so two of these can be in flight at once, and the older one
   * landing last would apply `checkingForExistenceOfOptions` with its stale
   * `listFilter` and rewrite the URL to a filter the user had already navigated
   * away from. It has to be superseded along with the request that issued it.
   */
  const getFilterOptionsFromBackend = (
    listFilter: typeof filter,
    isStale: () => boolean = () => unmount.current
  ) => {
    setSelectLoader(true);
    getRequest('/cloud/management/logs/filters')
      .then(res => {
        if (!isStale()) {
          const { body } = res;
          setFilters({
            ...filters,
            ...body
          });
          const newCheckingList = checkingForExistenceOfOptions(
            listFilter,
            setFilter,
            body
          );

          if (newCheckingList) {
            setPrevStateFilter(newCheckingList);
            newCheckingList &&
              replaceHistoryCML(
                '',
                filter,
                undefined,
                history,
                'validation',
                newCheckingList,
                changeFilter,
                parsConfigForHistory(tableConf)
              );
          }
          setSelectLoader(false);
        }
      })
      .catch(err => {
        if (!isStale()) {
          console.log(err);
          // Clear the spinner the request turned on, or the dropdowns stay
          // loading until some later poll happens to succeed.
          setSelectLoader(false);
        }
      });
  };

  const requestForFilteredData = (listFilter = filter) => {
    const inProgressStatusFilterIndex = (listFilter.status as string[]).indexOf(
      'In Progress'
    );
    if (inProgressStatusFilterIndex !== -1) {
      (listFilter.status as string[])[inProgressStatusFilterIndex] =
        'inprogress';
    }

    const linkForRequest = `/cloud/management/logs/list?page=${listFilter.currentPage}&limit=${listFilter.itemsPerPage}`;

    const REQ_BODY = createReqBody(listFilter as typeof defaultFilters);

    const requestId = ++latestRequestId.current;
    const isStale = () =>
      unmount.current || requestId !== latestRequestId.current;

    postRequest(linkForRequest, REQ_BODY)
      .then(res => {
        if (!isStale()) {
          const formatData = res.body.data.map((log: any) => {
            const updatedByName = {
              fullName: log.updatedByName,
              userEmail: log.updatedByEmail
            };
            return { ...log, updatedByName };
          });
          setTableData(formatData);
          setResults(res.body);
          setDataConnection(true);
          setLoader(false);
          setLoaderTable(false);
          getFilterOptionsFromBackend(listFilter, isStale);
        }
      })
      .catch(err => {
        // A superseded request must not clear the loader or flag the connection
        // as down — the newer request in flight owns both.
        if (!isStale()) {
          setDataConnection(false);
          setLoader(false);
          setLoaderTable(false);
          console.log(err);
        }
      });
  };
  const delayedRequestForFilteredData = debounce(
    requestForFilteredData,
    REQUEST_FOR_FILTERED_DATA_DELAY
  );

  const exportFunction = () =>
    onChangeExportButton(exportConfig, tableConfigDisplay);

  const resetAllfilters = (
    reload?: boolean | undefined,
    onlyFilterLabel?: boolean | undefined,
    onClickDefault?: boolean | undefined,
    modeClearAll?: boolean | undefined
  ) => {
    const newValueFilters = clearOrDefaultFilters(modeClearAll, defaultFilters);

    setFilter({ ...newValueFilters, ...defaultNavigation });

    if ((changeFilter === 'Default' || onClickDefault) && !modeClearAll) {
      setVisibleFilterSave(false);
      setVisibleFilterEdit(false);
      setTableConf(JSON.parse(JSON.stringify(tableConfiguration)));
    } else {
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    }
    if (!onlyFilterLabel) {
      setChangeFilter('Default');
      setTableConf(JSON.parse(JSON.stringify(tableConfiguration)));
    }

    setTrigerResetDatePicker(!triggerResetDatePicker);
    if (!reload) {
      setStartFilter(!startFilter);
    }
  };

  const handleSortable = (field: any) => {
    setDirection(direction === 1 ? -1 : 1);
    setFilter({
      ...filter,
      fieldName: field
    });
    setStartFilter(!startFilter);
  };

  useEffect(() => {
    if (contentState.beaconStatus.length) {
      let progressUpdated = false;
      const realTimeData = tableData.map((log: any) => {
        let isUpdate = contentState.beaconStatus?.find(
          (socketRec: any) =>
            socketRec.networkBeaconId === log.lastBeacon?.networkBeaconId &&
            socketRec.networkId === log.networkId
        );
        if (isUpdate) {
          progressUpdated = true;
          if (isUpdate?.networkBeaconStatus === 'success') {
            return {
              ...log,
              status: 'finished (success)'
            };
          } else {
            return {
              ...log,
              lastBeacon: {
                networkBeaconId: isUpdate?.networkBeaconId
              },
              status: isUpdate?.networkBeaconStatus,
              progress: isUpdate?.networkBeaconProgress
                ? isUpdate?.networkBeaconProgress + '%'
                : ''
            };
          }
        } else return log;
      });

      if (progressUpdated) {
        setTableData(realTimeData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentState]);

  const hasInProgressRows = (tableData as any[]).some(log =>
    isInProgressCMStatus(log?.status)
  );

  /*
   * Held in a ref so the interval always refetches with the filter currently on
   * screen. Closing over the function directly would pin the poll to whatever
   * the filter was when the first unfinished row appeared.
   */
  const refetchRef = useRef(requestForFilteredData);
  refetchRef.current = requestForFilteredData;

  useEffect(() => {
    if (!hasInProgressRows) return undefined;

    const timer = setInterval(() => {
      if (!unmount.current) {
        refetchRef.current();
      }
    }, IN_PROGRESS_POLL_INTERVAL);

    return () => clearInterval(timer);
  }, [hasInProgressRows]);

  if (loader) {
    return <Loading />;
  }

  if (!dataConnection) {
    return <EmptyState message="connect" />;
  }
  return (
    <>
      <PageTitle
        title={t('activity:cloudManagementLogs.TITLE')}
        subtitle={t('activity:cloudManagementLogs.SUBTITLE')}
      />
      <div className={classes.filters_main}>
        <InputSearch
          placeholder={t('activity:cloudManagementLogs.SEARCH_PLACEHOLDER')}
          value={filter.search}
          onChange={handleFilterInput}
          onClearSearch={onClearSearch}
          className={classes.filter}
          disabled={loaderTable}
        />
        <FilterButton active={filterButton} onClick={handleFilterButton} />
        <SaveFilterBlock
          setFilterButton={setFilterButton}
          visibleFilterSave={visibleFilterSave}
          setVisibleFilterSave={setVisibleFilterSave}
          visibleFilterEdit={visibleFilterEdit}
          setVisibleFilterEdit={setVisibleFilterEdit}
          filter={filter}
          setFilter={setFilter}
          filterBackend={filterBackend}
          setFilterBackend={setFilterBackend}
          changeFilter={changeFilter}
          setChangeFilter={setChangeFilter}
          resetAllfilters={resetAllfilters}
          defaultNavigation={defaultNavigation}
          trigerResetDatePicker={triggerResetDatePicker}
          setTrigerResetDatePicker={setTrigerResetDatePicker}
          startFilter={startFilter}
          setStartFilter={setStartFilter}
          defaultFilters={defaultFilters}
          tableConf={tableConf}
          defaulTableConf={tableConfiguration}
          setTableConf={setTableConf}
          triggerSaveTableSettings={triggerSaveTableSettings}
        />
      </div>

      <FiltersBlock
        filter={filter}
        setFilter={setFilter}
        startFilter={startFilter}
        setStartFilter={setStartFilter}
        filters={filters}
        filterButton={filterButton}
        trigerResetDatePicker={triggerResetDatePicker}
        resetAllfilters={resetAllfilters}
        selectLoader={selectLoader}
        setVisibleFilterSave={setVisibleFilterSave}
        setVisibleFilterEdit={setVisibleFilterEdit}
        changeFilter={changeFilter}
      />
      {/* Owned by useTableSettings, which stages the action; a hook cannot
          render, and the grid's action config has no slot for JSX. */}
      {confirmModal}
      <Grid>
        {results?.totalRecords ? (
          <DataGrid
            autoHeight={true}
            lazyLoading={true}
            loading={loaderTable}
            tableConfig={tableConfigDisplay}
            numOfRowsOptions={numOfRowsOptions}
            tableData={{
              page: results?.page || 0,
              totalPages: results?.totalPages || 0,
              totalRecords: results?.totalRecords || 0,
              data: tableData || []
            }}
            initialState={{
              pinnedColumns: {
                left: [tableConfiguration[0].field],
                right: ['actions']
              }
            }}
            paginationProps={{
              page: filter.currentPage - 1,
              count: results?.totalRecords || 0,
              rowsPerPage: numOfRowsOptions.find(
                (item: { name: number; selected: boolean }) =>
                  item.selected === true
              )!.name,
              onChangePage: (event, page) => {
                changeCurrentPage(page + 1);
              },
              hideNavigation: !results?.totalRecords,
              hideRowCount: !results?.totalRecords
            }}
            toolbarProps={{
              title1: t('activity:cloudManagementLogs.TOOLBAR_TITLE'),
              title2: `(${results?.totalRecords || 0})`,
              exportTooltipText: t('activity:cloudManagementLogs.EXPORT'),
              tableSettingsTooltipText: t(
                'activity:cloudManagementLogs.TABLE_SETTINGS'
              ),
              rowSelectionTooltipText: t(
                'activity:cloudManagementLogs.QUANTITY_OF_ITEMS'
              ),
              tableSettingsListItems: tableSettingsDisplay,
              rowSelectionChanged,
              tableSettingsChanged,
              handleTableSettingsMenuItemClick,
              handleSearchChangeCallback,
              onChangeNumOfRows: handleChangeNumOfRows,
              onSortEnd,
              exportButtonProps: {
                onClick: exportFunction
              }
            }}
            rowCount={results?.totalRecords || 0}
            onSortModelChange={a => {
              handleSortable(a[0].field);
            }}
            sortingMode={'server'}
            paginationMode={'server'}
          />
        ) : (
          <EmptyState />
        )}
      </Grid>
    </>
  );
};

export default CloudManagementLogs;
